import React, { useState } from "react";
import { api } from "@/lib/api";
import { PrimaryButton, GhostButton, Card } from "@/components/Primitives";
import { toast } from "sonner";

const STATUS_STYLE = {
  "Previewed": "text-zinc-300 border-white/20",
  "Imported": "text-[#00E676] border-[#00E676]/40",
  "Imported With Warnings": "text-[#FFB300] border-[#FFB300]/40",
  "Completed — No New Records": "text-[#4FC3F7] border-[#4FC3F7]/40",
  "Failed": "text-[#FF3B30] border-[#FF3B30]/40",
};

const StatusBadge = ({ status }) => (
  <span className={`inline-block border px-2 py-0.5 text-[10px] uppercase tracking-wider whitespace-nowrap ${STATUS_STYLE[status] || "text-zinc-300 border-white/20"}`} data-testid="batch-status-badge">{status}</span>
);

const fmtDate = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return s; }
};

export default function BulkImportModal({ open, onClose, onImported }) {
  const [rows, setRows] = useState([]); // JSON rows only
  const [csvText, setCsvText] = useState(""); // raw CSV; parsed server-side
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [filename, setFilename] = useState("");
  const [sourceType, setSourceType] = useState("csv");
  const [busy, setBusy] = useState(false);
  const [allowDup, setAllowDup] = useState(false);
  const [tab, setTab] = useState("import");
  const [batches, setBatches] = useState(null);
  const [loadingHist, setLoadingHist] = useState(false);
  if (!open) return null;

  const ready = sourceType === "json" ? rows.length > 0 : csvText.length > 0;

  const sum = preview?.summary;
  const netNew = sum ? sum.valid + (allowDup ? sum.duplicates : 0) : 0;
  // All importable rows already exist (duplicates) and there is nothing erroring out -> still processable.
  const onlyDuplicates = sum ? (netNew === 0 && sum.duplicates > 0 && sum.errors < sum.total) : false;
  const canSubmit = netNew > 0 || onlyDuplicates;
  const submitLabel = netNew > 0 ? `Import ${netNew}` : "Process (No New Records)";

  const reset = () => { setRows([]); setCsvText(""); setPreview(null); setResult(null); setFilename(""); setAllowDup(false); };
  const close = () => { reset(); onClose?.(); };

  const loadHistory = async () => {
    setLoadingHist(true);
    try {
      const r = await api.get("/candidates/import-batches");
      setBatches(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load history"); }
    finally { setLoadingHist(false); }
  };
  const showHistory = () => { setTab("history"); loadHistory(); };

  const handleFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setFilename(f.name);
    setPreview(null); setResult(null);
    const txt = await f.text();
    try {
      if (f.name.toLowerCase().endsWith(".json")) {
        const parsed = JSON.parse(txt);
        setRows(Array.isArray(parsed) ? parsed : parsed.rows || []);
        setCsvText("");
        setSourceType("json");
      } else {
        // Send raw CSV to the backend; the server parses it robustly.
        setCsvText(txt);
        setRows([]);
        setSourceType("csv");
      }
    } catch (err) { toast.error("Parse failed: " + err.message); }
  };

  const payload = (mode) =>
    sourceType === "csv"
      ? { mode, source_type: "csv", filename, csv_text: csvText, import_duplicates: allowDup }
      : { mode, source_type: "json", filename, rows, import_duplicates: allowDup };

  const runPreview = async () => {
    setBusy(true);
    try {
      const r = await api.post("/candidates/bulk-import", payload("preview"));
      setPreview(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/candidates/import-template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "candidate_import_template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Template download failed"); }
  };

  const doImport = async () => {
    const msg = netNew > 0
      ? `Import ${netNew} candidates? Duplicates ${allowDup ? "WILL" : "will NOT"} be imported.`
      : "All rows already exist — no new records will be created. Process anyway?";
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const r = await api.post("/candidates/bulk-import", payload("import"));
      setResult(r.data);
      if (r.data.status === "Failed") toast.error("Import failed — no records created");
      else toast.success(`${r.data.status} · imported ${r.data.imported}, skipped ${r.data.skipped}`);
      onImported?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-8" data-testid="bulk-import-modal">
      <Card className="max-w-3xl w-full max-h-[85vh] overflow-y-auto p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Analyst Workflow</div>
            <h2 className="font-heading text-xl tracking-tighter">Bulk Import Candidates</h2>
            <p className="text-xs text-zinc-500 mt-1">Accepted files: <span className="text-zinc-300">CSV</span> or <span className="text-zinc-300">JSON</span>. Required columns: candidate_name, buyer_contracting_authority, country, official_tender_link, product_category. CSV supports quoted fields and commas inside quoted fields.</p>
            <button onClick={downloadTemplate} data-testid="bulk-template-download" className="mt-2 text-[11px] text-[#FFB300] hover:underline">↓ Download CSV Template</button>
          </div>
          <button onClick={close} className="text-zinc-500 hover:text-white" data-testid="bulk-import-close">×</button>
        </div>

        <div className="flex gap-1 border-b border-white/10 mb-4">
          {[["import", "Import"], ["history", "History"]].map(([k, label]) => (
            <button key={k} data-testid={`bulk-tab-${k}`}
              onClick={() => (k === "history" ? showHistory() : setTab("import"))}
              className={`px-3 py-2 text-[11px] uppercase tracking-widest transition-colors ${tab === k ? "text-[#FFB300] border-b-2 border-[#FFB300]" : "text-zinc-500 hover:text-zinc-300"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "import" && (<>
        <input type="file" accept=".csv,.json" onChange={handleFile} data-testid="bulk-import-file"
          className="text-xs text-zinc-400 file:bg-[#FFB300] file:text-black file:border-0 file:px-3 file:py-1.5 file:font-bold file:uppercase file:text-[10px] file:tracking-widest file:mr-3" />

        {ready && !preview && !result && (
          <div className="mt-4 flex justify-between items-center">
            <span className="text-xs text-zinc-400 font-mono-data" data-testid="bulk-file-detected">
              {sourceType === "csv" ? `${filename || "file"} loaded · CSV (parsed on import)` : `${rows.length} rows detected · JSON`}
            </span>
            <PrimaryButton onClick={runPreview} disabled={busy} data-testid="bulk-preview-btn">{busy ? "Validating…" : "Preview"}</PrimaryButton>
          </div>
        )}

        {preview && !result && (
          <div className="mt-4">
            <div className="grid grid-cols-5 gap-2 mb-3 text-xs" data-testid="bulk-preview-summary">
              {["total", "valid", "duplicates", "errors", "warnings"].map((k) => (
                <div key={k} className="border border-white/10 p-2">
                  <div className="text-[9px] uppercase tracking-widest text-zinc-500">{k}</div>
                  <div className="font-heading text-xl" data-testid={`bulk-summary-${k}`}>{preview.summary[k] ?? 0}</div>
                </div>
              ))}
            </div>
            <div className="border border-white/10 max-h-[40vh] overflow-y-auto">
              <table className="w-full text-[11px]">
                <thead className="bg-white/[0.04]"><tr><th className="px-2 py-1 text-left">#</th><th className="text-left">Status</th><th className="text-left">Name / Issues</th></tr></thead>
                <tbody>
                  {preview.rows.map((p) => (
                    <tr key={p.row} className="border-t border-white/5" data-testid={`bulk-preview-row-${p.row}`}>
                      <td className="px-2 py-1 font-mono-data text-zinc-500">{p.row}</td>
                      <td className="px-2 py-1"><span className={p.status === "valid" ? "text-[#00E676]" : p.status === "duplicate" ? "text-[#FFB300]" : "text-[#FF3B30]"}>{p.status}</span></td>
                      <td className="px-2 py-1 text-zinc-300">{p.normalized.candidate_name || "(no name)"} {p.missing?.length > 0 && <span className="text-[#FF3B30] text-[10px]">missing: {p.missing.join(", ")}</span>}{p.duplicate_of && <span className="text-[#FFB300] text-[10px]"> dup: {p.duplicate_of}</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <label className="flex items-center gap-2 mt-3 text-xs text-zinc-400">
              <input type="checkbox" checked={allowDup} onChange={(e) => setAllowDup(e.target.checked)} data-testid="bulk-allow-dup" /> Also import {preview.summary.duplicates} possible duplicates
            </label>
            <div className="flex justify-end gap-2 mt-4">
              <GhostButton onClick={close}>Cancel</GhostButton>
              <PrimaryButton onClick={doImport} disabled={busy || !canSubmit} data-testid="bulk-import-btn">{busy ? "Importing…" : submitLabel}</PrimaryButton>
            </div>
          </div>
        )}

        {result && (
          <div className="mt-4" data-testid="bulk-import-result">
            <div className={`border p-4 ${STATUS_STYLE[result.status] || "text-zinc-300 border-white/20"}`}>
              <div className="text-[9px] uppercase tracking-widest text-zinc-500 mb-1">Batch Status</div>
              <div className="font-heading text-lg" data-testid="import-result-status">{result.status}</div>
              <div className="text-xs text-zinc-400 mt-2 font-mono-data">
                Imported {result.imported} · Skipped duplicates {result.skipped} · Total {result.summary?.total ?? 0}
              </div>
              {result.status === "Completed — No New Records" && (
                <div className="text-[11px] text-zinc-500 mt-2">All rows already existed — processed successfully with no new records created.</div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <PrimaryButton onClick={close} data-testid="bulk-result-done">Done</PrimaryButton>
            </div>
          </div>
        )}
        </>)}

        {tab === "history" && (
          <div data-testid="bulk-history">
            {loadingHist && <div className="text-xs text-zinc-500 py-8 text-center">Loading history…</div>}
            {!loadingHist && batches && batches.length === 0 && (
              <div className="text-xs text-zinc-500 py-8 text-center" data-testid="bulk-history-empty">No imports yet.</div>
            )}
            {!loadingHist && batches && batches.length > 0 && (
              <div className="border border-white/10 max-h-[55vh] overflow-auto">
                <table className="w-full min-w-[780px] text-[11px]">
                  <thead className="bg-white/[0.04] sticky top-0">
                    <tr className="text-left text-zinc-500 uppercase text-[9px] tracking-widest">
                      <th className="px-2 py-2">When</th><th className="px-2">File</th><th className="px-2">By</th>
                      <th className="px-2">Type</th><th className="px-2">Status</th>
                      <th className="px-2 text-right">Total</th><th className="px-2 text-right">Imported</th>
                      <th className="px-2 text-right">Skipped</th><th className="px-2 text-right">Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batches.map((b) => (
                      <tr key={b.id} className="border-t border-white/5" data-testid={`bulk-history-row-${b.id}`}>
                        <td className="px-2 py-2 font-mono-data text-zinc-500 whitespace-nowrap">{fmtDate(b.created_at)}</td>
                        <td className="px-2 text-zinc-300 max-w-[150px] truncate" title={b.filename || ""}>
                          {b.filename || "—"}
                          {b.notes && <div className="text-[9px] text-zinc-600 truncate" title={b.notes}>{b.notes}</div>}
                        </td>
                        <td className="px-2 text-zinc-400 max-w-[150px] truncate" title={b.uploader_email || b.uploaded_by || ""}>{b.uploader_email || b.uploaded_by || "—"}</td>
                        <td className="px-2 uppercase text-zinc-400">{b.source_type || "—"}</td>
                        <td className="px-2 py-1.5"><StatusBadge status={b.status} /></td>
                        <td className="px-2 text-right font-mono-data text-zinc-300">{b.total_rows ?? 0}</td>
                        <td className="px-2 text-right font-mono-data text-[#00E676]">{b.imported_rows ?? 0}</td>
                        <td className="px-2 text-right font-mono-data text-[#FFB300]">{b.skipped_duplicates ?? 0}</td>
                        <td className="px-2 text-right font-mono-data text-[#FF3B30]">{b.validation_errors ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="text-[10px] text-zinc-600 mt-3">Read-only history of past bulk imports.</div>
          </div>
        )}
      </Card>
    </div>
  );
}
